<template>
  <div class="enhanced-swipe-back">
    <div 
      class="enhanced-swipe-back__edge"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchCancel"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { navigationManager } from '@/capacitor/exited/navigationmanager'
import { exitManager } from '@/capacitor/exited/exitmanager'
import { useRouter } from 'vue-router'

// 配置常量
const EDGE_WIDTH = 30          // 边缘触发区域宽度
const TRIGGER_DISTANCE = 80    // 触发返回的最小距离
const MAX_DISTANCE = 200       // 最大滑动距离
const HINT_DURATION = 3000     // 提示显示时长
// 路由相关
const router = useRouter()
// 响应式状态
const startX = ref(0)           // 触摸开始X坐标
const startY = ref(0)           // 触摸开始Y坐标
const currentX = ref(0)         // 当前触摸X坐标
const currentY = ref(0)         // 当前触摸Y坐标
const isActive = ref(false)     // 是否正在滑动
const showIndicator = ref(false) // 是否显示返回指示器
const startTime = ref(0)        // 触摸开始时间
// 退出相关状态
const showExitConfirm = ref(false) // 是否显示退出确认
const exitProgress = ref(0)        // 退出进度
// 防止重复触发的标志
let lastTriggerTime = 0          // 上次触发时间
const TRIGGER_COOLDOWN = 500     // 触发冷却时间（毫秒）

// 计算属性
const swipeDistance = computed(() => {
  const deltaX = currentX.value - startX.value
  return Math.max(0, Math.min(deltaX, MAX_DISTANCE))
})

const swipeProgress = computed(() => 
  Math.min(swipeDistance.value / TRIGGER_DISTANCE, 1)
)

// 检查是否为根页面
const isRootPage = computed(() => {
  const currentPath = router.currentRoute.value.path
  // 定义根页面路由，这些页面会触发退出操作而不是返回操作
  const rootPages = [
   // '/member',   // 会员页
    '/logs',      // 日志页
    '/login',    // 登录页
  ]
  // 只有这些固定根页面才视为根页面
  // 动态页面（/page/*）的根页面判断由 navigationManager 处理
  const isRoot = rootPages.includes(currentPath)
  console.log(`[SwipeBack] Current path: ${currentPath}, is root: ${isRoot}`)
  return isRoot
})

const indicatorText = computed(() => {
  if (isRootPage.value) {
    return swipeProgress.value >= 1 ? '释放退出' : '继续滑动退出'
  } else {
    return swipeProgress.value >= 1 ? '释放返回' : '继续滑动'
  }
})

const contentStyle = computed(() => ({
  transform: `translateX(${swipeDistance.value}px)`,
  transition: isActive.value ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  filter: isActive.value && swipeDistance.value > 20 ? `brightness(${1 - swipeProgress.value * 0.1})` : 'none'
}))

// 事件处理函数
const handleTouchStart = (e) => {
  const touch = e.touches[0]
  const touchX = touch.clientX
  const touchY = touch.clientY
  console.log(`[SwipeBack] touchstart x=${touchX}, y=${touchY}, edge=${EDGE_WIDTH}`)
  
  // 只有从屏幕左边缘开始的触摸才有效
  if (touchX <= EDGE_WIDTH) {
    startX.value = touchX
    startY.value = touchY
    currentX.value = touchX
    currentY.value = touchY
    startTime.value = Date.now()
    isActive.value = true
    console.log('[SwipeBack] touchstart accepted')
    
  }
}

const handleTouchMove = (e) => {
  if (!isActive.value) return
  
  const touch = e.touches[0]
  currentX.value = touch.clientX
  currentY.value = touch.clientY
  
  const deltaX = currentX.value - startX.value
  const deltaY = Math.abs(currentY.value - startY.value)
  
  // 如果垂直滑动距离过大，取消水平滑动
  if (deltaY > 50 && deltaX < 30) {
    resetSwipeState()
    return
  }
  
  // 如果是有效的水平滑动，阻止默认行为
  if (deltaX > 10) {
    e.preventDefault()
    showIndicator.value = swipeProgress.value >= 0.3
  }
}

const handleTouchEnd = () => {
  if (!isActive.value) return

  const swipeTime = Date.now() - startTime.value
  const swipeVelocity = swipeDistance.value / swipeTime

  // 根据滑动距离和速度判断是否触发返回
  const shouldTriggerBack = swipeProgress.value >= 1 ||
    (swipeProgress.value >= 0.5 && swipeVelocity > 0.3)

  console.log(`[SwipeBack] Touch end - Distance触摸: ${swipeDistance.value}, 进展: ${swipeProgress.value}, 速度: ${swipeVelocity}, 是否触发返回: ${shouldTriggerBack}`)
  console.log(`[SwipeBack] 当前路径: ${router.currentRoute.value.path}, 是否为根页面: ${isRootPage.value}`)

  if (shouldTriggerBack) {
    // 检查冷却时间，防止重复触发
    const now = Date.now()
    if (now - lastTriggerTime < TRIGGER_COOLDOWN) {
      console.log(`[SwipeBack] 在冷却期内，忽略此次触发`)
      resetSwipeState()
      return
    }
    lastTriggerTime = now

    console.log(`[SwipeBack] 触发返回`)

    const EXIT_GESTURE_THRESHOLD = {
      MIN_DISTANCE: 120,
      MIN_VELOCITY: 0.5,
      MIN_PROGRESS: 0.8
    }

    const computedQualifiedExitGesture = swipeDistance.value >= EXIT_GESTURE_THRESHOLD.MIN_DISTANCE &&
                                      swipeVelocity >= EXIT_GESTURE_THRESHOLD.MIN_VELOCITY &&
                                      swipeProgress.value >= EXIT_GESTURE_THRESHOLD.MIN_PROGRESS

    console.log(`[SwipeBack] computedQualifiedExitGesture=${computedQualifiedExitGesture}`)

    // 添加触觉反馈（如果支持）
    if ('vibrate' in navigator) {
      navigator.vibrate(50)
    }

    navigationManager.triggerBack(true)
  }

  resetSwipeState()
}

const handleTouchCancel = () => {
  resetSwipeState()
}

// 辅助函数
const resetSwipeState = () => {
  isActive.value = false
  showIndicator.value = false
  startX.value = 0
  startY.value = 0
  currentX.value = 0
  currentY.value = 0
  startTime.value = 0
}

// 退出确认相关函数
const handleExitIntentSet = () => {
  showExitConfirm.value = true
  exitProgress.value = 0

  // 启动进度条动画
  const startTime = Date.now()
  const duration = exitManager.getConfig().confirmTimeout

  const updateProgress = () => {
    const elapsed = Date.now() - startTime
    const progress = Math.min((elapsed / duration) * 100, 100)
    exitProgress.value = progress

    if (progress < 100 && showExitConfirm.value) {
      requestAnimationFrame(updateProgress)
    }
  }

  requestAnimationFrame(updateProgress)
}

const handleExitIntentCleared = () => {
  showExitConfirm.value = false
  exitProgress.value = 0
}

// 生命周期钩子
onMounted(() => {
  // 监听退出意图事件
  window.addEventListener('退出意图已设置', handleExitIntentSet)
  window.addEventListener('退出意图已清除', handleExitIntentCleared)
})

onUnmounted(() => {
  // 清理事件监听器
  window.removeEventListener('退出意图已设置', handleExitIntentSet)
  window.removeEventListener('退出意图已清除', handleExitIntentCleared)
})

// 组件属性
defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

// 定义组件名称
defineOptions({
  name: 'SwipeBack'
})
</script>

<style scoped>
.enhanced-swipe-back {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 10000;
  background: transparent;
  width: 100vw;
  pointer-events: none;
}

.enhanced-swipe-back__edge {
  position: absolute;
  top: 0;
  left: 0;
  width: 30px;
  height: 100vh;
  pointer-events: auto;
  background: transparent;
}

/* 退出确认提示 */
.exit-confirm-overlay {
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1003;
  pointer-events: none;
}

.exit-confirm-message {
  background: rgba(255, 87, 34, 0.95);
  color: white;
  padding: 16px 24px;
  border-radius: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(255, 87, 34, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 200px;
}

.exit-icon {
  font-size: 24px;
  animation: pulse 1s infinite;
}

.exit-text {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.exit-progress-bar {
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 4px;
}

.exit-progress {
  height: 100%;
  background: linear-gradient(90deg, #fff, #ffeb3b);
  border-radius: 2px;
  transition: width 0.1s linear;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* 滑动进度条 */
.swipe-progress {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  z-index: 1001;
  transition: opacity 0.2s ease;
  box-shadow: 0 1px 3px rgba(76, 175, 80, 0.3);
}

/* 返回指示器 */
.back-indicator {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 500;
  z-index: 1002;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

/* 边缘提示 */
/* 内容容器 */
.content-wrapper {
  width: 100%;
  height: 100%;
  will-change: transform;
}

/* 动画效果 */
.fade-enter-active, .fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

/* 滑动上升动画 */
.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-up-enter-from, .slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(30px);
}

.slide-up-enter-to, .slide-up-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* 响应式适配 */
@media (max-width: 480px) {
  .back-indicator {
    font-size: 14px;
    padding: 10px 16px;
  }

  .exit-confirm-message {
    padding: 12px 20px;
    min-width: 180px;
  }

  .exit-text {
    font-size: 13px;
  }

  .exit-icon {
    font-size: 20px;
  }
}
</style>
